import React from 'react'
import { graphql, Link } from 'gatsby'
import Img from 'gatsby-image'

import Layout from '../components/layout'
import SEO from '../components/seo'
import styles from './morph-te.module.scss'

const VimeoPlayer = ({ video, hash, title, autoPlay = false, muted = false, loop = false, fourThree = false }) => {
    const query = [
        hash && `h=${hash}`,
        'badge=0',
        'autopause=0',
        'player_id=0',
        'app_id=58479',
        autoPlay && 'autoplay=1',
        muted && 'muted=1',
        loop && 'loop=1'
    ].filter(Boolean).join('&')

    return (
        <div className={`${styles.videoFrame}${fourThree ? ` ${styles.videoFrameFourThree}` : ''}`}>
            <iframe
                src={`https://player.vimeo.com/video/${video}?${query}`}
                title={title}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
            />
        </div>
    )
}

const MorphTe = ({ data }) => {
    const { markdown, hero, introductionImage, humanScale, detail, movementOne, movementTwo, movementThree } = data
    const { frontmatter, excerpt } = markdown

    return (
        <Layout>
            <SEO
                title={frontmatter.title}
                description={excerpt}
                keywords={frontmatter.tags}
                image={hero.childImageSharp.fluid}
            />

            <article className={styles.page}>
                <header className={styles.hero}>
                    <Img
                        className={styles.heroImage}
                        fluid={hero.childImageSharp.fluid}
                        alt="MORPH TE glowing inside the stone gallery at Burg Taggenbrunn, with Mitchell Nordine in the foreground"
                        loading="eager"
                    />
                    <div className={styles.heroShade} />
                    <div className={styles.heroCopy}>
                        <p className={styles.eyebrow}>Permanent kinetic sculpture · Austria, 2021</p>
                        <h1>MORPH <span>TE</span></h1>
                        <p className={styles.heroStatement}>A machine that appears alive.</p>
                    </div>
                </header>

                <section className={styles.introduction}>
                    <div className={styles.sectionLabel}>The work</div>
                    <h2>Light, movement and sound become a single living body.</h2>
                    <Img
                        className={styles.introductionImage}
                        fluid={introductionImage.childImageSharp.fluid}
                        alt="MORPH TE illuminated in blue and white inside the stone gallery"
                    />
                    <div className={styles.introductionCopy}>
                        <p>
                            MORPH TE turns 486 moving hexagonal cells into one monumental presence. Its surface expands, contracts and ripples as light travels across its skin—precise mechanical choreography that feels less like automation and more like breath.
                        </p>
                        <p>
                            Created by Augmentl Studio and MindBuffer as mentl.Lab, the work explores our deepening relationship with machines. It asks what happens when technology stops behaving like a tool and begins to suggest instinct, character and life.
                        </p>
                    </div>
                </section>

                <section className={styles.facts} aria-label="MORPH TE in numbers">
                    <div>
                        <strong>80,000+</strong>
                        <span>individually composed lights</span>
                    </div>
                    <div>
                        <strong>486</strong>
                        <span>precision motors</span>
                    </div>
                    <div>
                        <strong>01</strong>
                        <span>emergent organism</span>
                    </div>
                </section>

                <section className={styles.filmSection}>
                    <div className={styles.mediaHeading}>
                        <div>
                            <span className={styles.sectionLabel}>In motion</span>
                            <h2>Berlin preview</h2>
                        </div>
                        <p>Experience the full choreography of light, motion and sound.</p>
                    </div>
                    <VimeoPlayer
                        video="1215065506"
                        title="morph-te berlin preview"
                        autoPlay
                        muted
                        loop
                        fourThree
                    />
                </section>

                <section className={styles.officialFilm}>
                    <div className={styles.mediaHeading}>
                        <div>
                            <span className={styles.sectionLabel}>mentl.Lab</span>
                            <h2>Official film</h2>
                        </div>
                        <p>MORPH TE in its permanent home at ZEITRÄUM.</p>
                    </div>
                    <VimeoPlayer video="582736765" title="MORPH TE | mentl.Lab" />
                </section>

                <section className={styles.imageStatement}>
                    <Img
                        className={styles.statementImage}
                        fluid={humanScale.childImageSharp.fluid}
                        alt="A person standing beside the illuminated MORPH TE sculpture"
                    />
                    <div className={styles.statementCopy}>
                        <span className={styles.sectionLabel}>Artificial life</span>
                        <h2>Built from first principles.</h2>
                        <p>
                            Every visible gesture begins deep inside a structure of steel, hand-shaped copper, folded aluminium, precision carbon fibre and custom electronics. Each cell is simple on its own. Across the sphere, those cells become waves, pulses, breaths and moments of apparent intention.
                        </p>
                    </div>
                </section>

                <section className={styles.movementGallery} aria-label="MORPH TE at Burg Taggenbrunn">
                    <figure>
                        <Img fluid={movementOne.childImageSharp.fluid} alt="A visitor standing beside MORPH TE in the dark stone gallery" />
                    </figure>
                    <figure>
                        <Img fluid={movementTwo.childImageSharp.fluid} alt="The illuminated hexagonal surface of MORPH TE seen at close range" />
                    </figure>
                    <figure>
                        <Img fluid={movementThree.childImageSharp.fluid} alt="MORPH TE glowing blue and violet inside the exhibition space" />
                    </figure>
                </section>

                <blockquote className={styles.quote}>
                    <p>“The breath in the lungs. The heart in the chest. The ghost in the machine.”</p>
                </blockquote>

                <section className={styles.engineering}>
                    <div className={styles.engineeringCopy}>
                        <span className={styles.sectionLabel}>The system</span>
                        <h2>A many-armed conductor.</h2>
                        <p>
                            MindBuffer developed the creative software and sound design that bind movement, illumination and music into one instrument. Mathematical fields, natural-system simulations and biologically inspired behaviours are translated into precise instructions for a distributed network of motors, lights and computers.
                        </p>
                        <div className={styles.disciplines}>
                            <span>Generative systems</span>
                            <span>Motion control</span>
                            <span>Light composition</span>
                            <span>Spatial sound</span>
                        </div>
                    </div>
                    <Img
                        className={styles.engineeringImage}
                        fluid={detail.childImageSharp.fluid}
                        alt="MORPH TE's illuminated hexagonal cells in close detail"
                    />
                </section>

                <section className={styles.documentary}>
                    <div className={styles.mediaHeading}>
                        <div>
                            <span className={styles.sectionLabel}>Documentary</span>
                            <h2>The kinetic super-sculpture</h2>
                        </div>
                        <p>Deutsche Welle visits MORPH TE and the artists behind it.</p>
                    </div>
                    <VimeoPlayer
                        video="1215379681"
                        hash="8403c00a5a"
                        title="morph-dw-documentary"
                        fourThree
                    />
                </section>

                <section className={styles.process}>
                    <div className={styles.processCopy}>
                        <span className={styles.sectionLabel}>Behind the work</span>
                        <h2>Presented at SIGGRAPH 2021.</h2>
                        <p>
                            At the world’s leading conference for computer graphics and interactive techniques, the team unpacked the years of hardware, firmware, software and sound design behind MORPH TE.
                        </p>
                    </div>
                    <VimeoPlayer video="567692797" title="mentl.Lab | MORPH TE | SIGGRAPH Presentation 2021" />
                </section>

                <section className={styles.credits}>
                    <div>
                        <span className={styles.sectionLabel}>Commission</span>
                        <p>
                            Created for ZEITRÄUM, curated by André Heller.<br />
                            Burg Taggenbrunn, Austria.
                        </p>
                    </div>
                    <div>
                        <span className={styles.sectionLabel}>Artists</span>
                        <dl>
                            <dt>Nicholas Perillo · Augmentl Studio</dt>
                            <dd>Original concept, industrial design, electronics engineering and fabrication</dd>
                            <dt>Joshua Batty · MindBuffer</dt>
                            <dd>Creative development, software development and sound design</dd>
                            <dt>Mitchell Nordine · MindBuffer</dt>
                            <dd>Creative development, firmware, software development and sound design</dd>
                        </dl>
                    </div>
                </section>

                <footer className={styles.projectFooter}>
                    <Link to="/works">Selected works <span aria-hidden="true">→</span></Link>
                </footer>
            </article>
        </Layout>
    )
}

export default MorphTe

export const query = graphql`
    query MorphTeQuery($slug: String!) {
        markdown: markdownRemark(fields: { slug: { eq: $slug } }) {
            excerpt(pruneLength: 200)
            frontmatter {
                title
                tags
            }
        }
        hero: file(relativePath: { eq: "images/morph-te/morph-te-hero.jpg" }) {
            childImageSharp {
                fluid(maxWidth: 3008, quality: 94) { ...GatsbyImageSharpFluid_withWebp }
            }
        }
        introductionImage: file(relativePath: { eq: "images/morph-te/morph-te-introduction.png" }) {
            childImageSharp {
                fluid(maxWidth: 900, quality: 92) { ...GatsbyImageSharpFluid_withWebp }
            }
        }
        humanScale: file(relativePath: { eq: "images/morph-te/morph-te-human-scale.jpg" }) {
            childImageSharp {
                fluid(maxWidth: 2500, quality: 92) { ...GatsbyImageSharpFluid_withWebp }
            }
        }
        detail: file(relativePath: { eq: "images/morph-te/morph-te-134.jpg" }) {
            childImageSharp {
                fluid(maxWidth: 1800, quality: 92) { ...GatsbyImageSharpFluid_withWebp }
            }
        }
        movementOne: file(relativePath: { eq: "images/morph-te/morph-te-gallery-human-scale.jpg" }) {
            childImageSharp {
                fluid(maxWidth: 1800, quality: 92) { ...GatsbyImageSharpFluid_withWebp }
            }
        }
        movementTwo: file(relativePath: { eq: "images/morph-te/morph-te-gallery-cell-detail.jpg" }) {
            childImageSharp {
                fluid(maxWidth: 1400, quality: 92) { ...GatsbyImageSharpFluid_withWebp }
            }
        }
        movementThree: file(relativePath: { eq: "images/morph-te/morph-te-gallery-room.jpg" }) {
            childImageSharp {
                fluid(maxWidth: 1400, quality: 92) { ...GatsbyImageSharpFluid_withWebp }
            }
        }
    }
`
